import React, { useState, useEffect } from "react";
import { getAllLogoUrls } from "./categoryServices";
import { Box, Typography, Card, CardContent, Dialog, DialogContent, DialogTitle, Button, DialogActions, Tooltip } from "@mui/material";
import { storage } from "./firebaseServices";
import { ref as storageRef, getDownloadURL as getDownloadURLSDK } from "firebase/storage";

function GetLogos() {
  const [urls, setUrls] = useState([]);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    const fetchUrls = async () => {
      try {
        const logoUrls = await getAllLogoUrls();
        setUrls(logoUrls);
      } catch (error) {
        console.error("Error fetching logos:", error);
      }
    };

    fetchUrls();
  }, []);

  const handleCardClick = (logo) => {
    setSelectedLogo(logo);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLogo(null);
  };

  const handleImgDownload = async (imageUrl, fileName, fullPath) => {
    try {
      let fetchUrl = imageUrl;

      // Prefer using the Storage SDK to create a fresh download URL for this specific storage path.
      if (fullPath) {
        try {
          const sdkUrl = await getDownloadURLSDK(storageRef(storage, fullPath));
          fetchUrl = sdkUrl;
        } catch (sdkErr) {
          console.warn("Storage SDK download URL failed, falling back to provided URL:", sdkErr);
          // fall back to provided imageUrl below
        }
      }

      // Force a fresh network fetch to avoid returning a cached 304 with a missing body.
      // Use no-store to ensure browser retrieves a full response body.
      if (!fetchUrl.includes("cachebust=")) {
        fetchUrl = fetchUrl + (fetchUrl.includes("?") ? "&" : "?") + "cachebust=" + Date.now();
      }

      const response = await fetch(fetchUrl, { cache: "no-store", mode: "cors" });

      if (!response.ok) throw new Error(`Network response was not ok (${response.status})`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      const inferredName = fileName || decodeURIComponent((imageUrl.split("/").pop() || "").split("?")[0]);
      link.href = url;
      link.download = inferredName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the image:", error);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ marginBottom: 5 }}>
        Official Driploc Logos
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {urls.map((logo, index) => (
          <Tooltip title="Click to view fullsize" key={index}>
            <Card
              key={index}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                maxWidth: 600,
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                },
              }}
              onClick={() => handleCardClick(logo)}
            >
              <CardContent>
                <Typography variant="h6">{logo.fileName}</Typography>
                <Box sx={{ p: 2, backgroundColor: "#e0e0e0", borderRadius: 2 }}>
                  <img src={logo.url} alt={logo.fileName} style={{ width: "140px", height: "auto", marginLeft: "10px" }} />
                </Box>
              </CardContent>
            </Card>
          </Tooltip>
        ))}

        {/* Dialog for showing selected logo */}
        <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="lg">
          <DialogTitle>{selectedLogo?.fileName}</DialogTitle>
          <DialogContent>
            {selectedLogo ? (
              <Box sx={{ textAlign: "center", marginBottom: 2 }}>
                <img src={selectedLogo.url} alt={selectedLogo.fileName} style={{ width: "600px", height: "auto" }} />
                <Box mt={2}>
                  <Tooltip title="Download the image">
                    <Button
                      variant="outlined"
                      onClick={() => handleImgDownload(selectedLogo.url, selectedLogo.fileName, selectedLogo.fullPath)}
                    >
                      Download
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No images available
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

export default GetLogos;
